<template>
  <div
    ref="rootModal"
    class="modal fade"
    tabindex="-1"
    data-bs-keyboard="false"
    data-bs-backdrop="static"
  >
    <div class="modal-dialog modal-lg">
      <div class="modal-content">

        <div class="modal-header">
          <h5 class="modal-title">
            <i class="bi bi-qr-code me-2"></i>
            Pagamento via PIX
          </h5>
          <button type="button" class="btn-close" @click="closeModal"></button>
        </div>

        <div class="modal-body">
          <div v-if="isProcessing" class="text-center py-5">
            <div class="spinner-border text-primary" role="status">
              <span class="visually-hidden">Carregando...</span>
            </div>
            <p class="mt-3 text-muted">Carregando dados do PIX...</p>
          </div>

          <div v-else-if="pixData">
            <!-- QR Code -->
            <div class="text-center mb-4">
              <div class="d-inline-block p-3 bg-light rounded">
                <img 
                  v-if="qrCodeSrc"
                  :src="qrCodeSrc"
                  alt="QR Code PIX"
                  class="img-fluid"
                  style="max-width: 300px;"
                >
                <div v-else class="text-muted">
                  <i class="bi bi-qr-code" style="font-size: 200px;"></i>
                </div>
              </div>
              <p class="text-muted mt-2">
                Escaneie o QR Code com o app do seu banco
              </p>
            </div>

            <!-- Código PIX -->
            <div class="mb-4">
              <label class="form-label fw-bold">Código PIX (Copia e Cola)</label>
              <div class="input-group">
                <input
                  ref="pixCodeInput"
                  type="text"
                  class="form-control font-monospace"
                  :value="pixData.pix_code"
                  readonly
                >
                <button 
                  class="btn btn-outline-secondary" 
                  type="button"
                  @click="copyPixCode"
                  :title="pixCodeCopied ? 'Copiado!' : 'Copiar código'"
                >
                  <i :class="pixCodeCopied ? 'bi bi-check-lg text-success' : 'bi bi-clipboard'"></i>
                </button>
              </div>
              <small v-if="pixCodeCopied" class="text-success">
                <i class="bi bi-check-circle"></i> Código copiado!
              </small>
            </div>

            <!-- Validade -->
            <div class="alert alert-info mb-4">
              <div class="d-flex align-items-center">
                <i class="bi bi-clock-history me-2"></i>
                <div>
                  <strong>Válido por:</strong> 
                  <span v-if="timeRemaining">{{ timeRemaining }}</span>
                  <span v-else-if="isExpired" class="text-danger">PIX expirado</span>
                  <span v-else>Validade não informada</span>
                </div>
              </div>
            </div>

            <!-- Status do Pagamento -->
            <div v-if="paymentStatus === 'S'" class="alert alert-success">
              <div class="d-flex align-items-center">
                <i class="bi bi-check-circle-fill me-2"></i>
                <div>
                  <strong>PIX pago com sucesso!</strong>
                  <p class="mb-0">A assinatura foi ativada.</p>
                </div>
              </div>
            </div>

            <div v-else-if="paymentStatus === 'P'" class="alert alert-warning">
              <div class="d-flex align-items-center">
                <i class="bi bi-hourglass-split me-2"></i>
                <div>
                  <strong>Aguardando pagamento</strong>
                  <p class="mb-0">Assim que o pagamento for confirmado, você será notificado.</p>
                </div>
              </div>
            </div>

            <div v-else-if="isExpired" class="alert alert-danger">
              <div class="d-flex align-items-center">
                <i class="bi bi-exclamation-triangle-fill me-2"></i>
                <div>
                  <strong>PIX expirado</strong>
                  <p class="mb-0">Este código PIX expirou. Entre em contato com o suporte.</p>
                </div>
              </div>
            </div>
          </div>

          <div v-else class="text-center py-5">
            <i class="bi bi-exclamation-circle text-warning" style="font-size: 48px;"></i>
            <p class="mt-3 text-muted">Dados do PIX não disponíveis.</p>
          </div>
        </div>

        <div class="modal-footer">
          <button type="button" class="btn btn-secondary" @click="closeModal">
            Fechar
          </button>
        </div>

      </div>
    </div>
  </div>
</template>

<script>
import http from "@/helpers/http";
import Alert from "@/helpers/alert";
import { toPixQrCodeDataUrl } from "@/helpers/pixQrCode";

export default {
  name: "PixPaymentModal",
  props: {
    pixData: {
      type: Object,
      default: null
    }
  },
  emits: ['onClose', 'paymentConfirmed'],
  data() {
    return {
      isProcessing: false,
      pixCodeCopied: false,
      paymentStatus: 'P', // P - Pendente, S - Pago, C - Cancelado
      statusCheckInterval: null,
      expiresAt: null,
    };
  },
  computed: {
    qrCodeSrc() {
      return toPixQrCodeDataUrl(this.pixData?.qr_code_base64);
    },
    timeRemaining() {
      if (!this.expiresAt) return null;
      
      const now = new Date();
      const expires = new Date(this.expiresAt);
      const diff = expires - now;
      
      if (diff <= 0) return null;
      
      const minutes = Math.floor(diff / 60000);
      const seconds = Math.floor((diff % 60000) / 1000);
      
      return `${minutes}:${seconds.toString().padStart(2, '0')}`;
    },
    isExpired() {
      if (!this.expiresAt) return false;
      return new Date(this.expiresAt) < new Date();
    }
  },
  watch: {
    pixData: {
      immediate: true,
      handler(newValue) {
        if (newValue) {
          this.expiresAt = newValue.expires_at || newValue.pix_expires_at || null;
          this.paymentStatus = 'P';
          // Não iniciar polling aqui, apenas quando modal for aberto
        }
      }
    }
  },
  mounted() {
    if (this.pixData) {
      this.expiresAt = this.pixData.expires_at || this.pixData.pix_expires_at || null;
    }

    this.$nextTick(() => {
      const el = this.$refs.rootModal;
      if (!el) {
        return;
      }
      this._onPixModalShown = () => {
        if (this.pixData && !this.isExpired && this.paymentStatus !== "S") {
          this.startPixStatusCheck();
        }
      };
      this._onPixModalHidden = () => {
        this.stopPixStatusCheck();
      };
      el.addEventListener("shown.bs.modal", this._onPixModalShown);
      el.addEventListener("hidden.bs.modal", this._onPixModalHidden);
    });
  },
  beforeUnmount() {
    this.stopPixStatusCheck();
    const el = this.$refs.rootModal;
    if (el && this._onPixModalShown) {
      el.removeEventListener("shown.bs.modal", this._onPixModalShown);
      el.removeEventListener("hidden.bs.modal", this._onPixModalHidden);
    }
  },
  methods: {
    open() {
      const el = this.$refs.rootModal;
      if (!el || typeof bootstrap === "undefined") {
        return;
      }
      try {
        bootstrap.Modal.getOrCreateInstance(el).show();
      } catch (e) {
        console.error(e);
      }
    },
    copyPixCode() {
      const input = this.$refs.pixCodeInput;
      if (input) {
        input.select();
        input.setSelectionRange(0, 99999); // Para mobile
        
        try {
          document.execCommand('copy');
          this.pixCodeCopied = true;
          setTimeout(() => {
            this.pixCodeCopied = false;
          }, 2000);
        } catch (err) {
          console.error('Erro ao copiar:', err);
          Alert.error('Erro ao copiar código PIX');
        }
      }
    },
    async checkPixPaymentStatus() {
      if (this.pixData?.platform_charge_id && this.pixData?.place_id) {
        try {
          const response = await http.get(
            `/places/${this.pixData.place_id}/platform-subscription/charges/${this.pixData.platform_charge_id}`,
          );
          const charge = response.data?.data || response.data;

          if (charge?.charge_status === 'paid') {
            this.paymentStatus = 'S';
            this.stopPixStatusCheck();
            this.$emit('paymentConfirmed');
            setTimeout(() => {
              this.closeModal();
            }, 3000);
          }
        } catch (error) {
          console.error('Erro ao verificar status da cobrança:', error);
        }

        return;
      }

      if (!this.pixData?.payment_id) {
        return;
      }

      try {
        const response = await http.get(`/status-pix?id=${this.pixData.payment_id}`);
        const status = response.data?.status || response.data?.payment_status;
        
        if (status) {
          this.paymentStatus = status;
          
          if (status === 'S') {
            // PIX foi pago
            this.stopPixStatusCheck();
            this.$emit('paymentConfirmed');
            
            // Fechar modal após alguns segundos
            setTimeout(() => {
              this.closeModal();
            }, 3000);
          }
        }
      } catch (error) {
        console.error('Erro ao verificar status do PIX:', error);
        // Não mostrar erro para o usuário, apenas logar
      }
    },
    startPixStatusCheck() {
      // Parar qualquer polling anterior
      this.stopPixStatusCheck();
      
      const modalElement = this.$refs.rootModal;
      if (!modalElement || !modalElement.classList.contains("show")) {
        return;
      }

      // Verificar status imediatamente
      this.checkPixPaymentStatus();
      
      // Verificar a cada 2 segundos
      this.statusCheckInterval = setInterval(() => {
        // Verificar se modal ainda está aberto
        const modalEl = this.$refs.rootModal;
        if (!modalEl || !modalEl.classList.contains("show")) {
          this.stopPixStatusCheck();
          return;
        }

        // Verificar se PIX expirou
        if (this.isExpired) {
          this.stopPixStatusCheck();
          return;
        }

        // Verificar se já foi pago
        if (this.paymentStatus === 'S') {
          this.stopPixStatusCheck();
          return;
        }

        this.checkPixPaymentStatus();
      }, 2000);
    },
    stopPixStatusCheck() {
      if (this.statusCheckInterval) {
        clearInterval(this.statusCheckInterval);
        this.statusCheckInterval = null;
      }
    },
    closeModal() {
      this.stopPixStatusCheck();
      const el = this.$refs.rootModal;
      if (el && typeof bootstrap !== "undefined") {
        try {
          const inst = bootstrap.Modal.getInstance(el);
          if (inst) {
            inst.hide();
          }
        } catch (e) {
          console.error(e);
        }
      }
      this.$emit("onClose");
    }
  }
}
</script>

<style scoped>
.font-monospace {
  font-family: 'Courier New', monospace;
  font-size: 0.875rem;
}
</style>
